import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Play, Database, ClipboardList, UserPlus, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

type TestType = 'quiz-only' | 'quiz-with-email' | 'quiz-with-lead';

export const AutomatedTestingPanel = () => {
  const [testing, setTesting] = useState(false);
  const [testType, setTestType] = useState<TestType>('quiz-only');
  const [testResults, setTestResults] = useState<any[]>([]);
  const { toast } = useToast();

  const runTest = async () => {
    setTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke('run-automated-quiz-test', {
        body: { testType }
      });
      
      if (error) throw error;
      
      setTestResults(prev => [data, ...prev]);
      
      toast({
        title: "✅ Test ukończony!",
        description: `${data.testUser.name} - ${data.testType}`,
      });
    } catch (error: any) {
      console.error('Test failed:', error);
      toast({
        title: "❌ Test failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setTesting(false);
    }
  };

  const seedTestData = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('seed-test-data');
      if (error) throw error;
      
      toast({
        title: "✅ Test data seeded!",
        description: `${data.count} test users loaded`
      });
    } catch (error: any) {
      toast({
        title: "❌ Seed failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Automated Quiz Testing System
          </CardTitle>
          <CardDescription>
            Automatyczne testy quiz z prawdziwymi danymi użytkowników (47 rekordów). 
            Generuje pełne raporty w produkcyjnej bazie danych.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Test Type Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Wybierz typ testu:</Label>
            <RadioGroup value={testType} onValueChange={(v) => setTestType(v as TestType)}>
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                <RadioGroupItem value="quiz-only" id="quiz-only" />
                <Label htmlFor="quiz-only" className="flex items-center gap-2 cursor-pointer flex-1">
                  <ClipboardList className="h-4 w-4" />
                  <div>
                    <div className="font-semibold">Quiz Only</div>
                    <div className="text-xs text-muted-foreground">Tylko quiz (bez konwersji na lead)</div>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                <RadioGroupItem value="quiz-with-email" id="quiz-with-email" />
                <Label htmlFor="quiz-with-email" className="flex items-center gap-2 cursor-pointer flex-1">
                  <FileText className="h-4 w-4" />
                  <div>
                    <div className="font-semibold">Quiz + Email</div>
                    <div className="text-xs text-muted-foreground">Quiz + email dla raportu (bez lead form)</div>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                <RadioGroupItem value="quiz-with-lead" id="quiz-with-lead" />
                <Label htmlFor="quiz-with-lead" className="flex items-center gap-2 cursor-pointer flex-1">
                  <FileText className="h-4 w-4" />
                  <div>
                    <div className="font-semibold">Quiz + Lead</div>
                    <div className="text-xs text-muted-foreground">Pełny flow: quiz → lead form → enrichment</div>
                  </div>
                </Label>
              </div>
              
            </RadioGroup>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button 
              onClick={runTest} 
              disabled={testing}
              size="lg"
              className="w-full"
            >
              {testing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run Test
                </>
              )}
            </Button>
            
            <Button 
              onClick={seedTestData}
              disabled={testing}
              variant="outline"
              size="lg"
            >
              <Database className="mr-2 h-4 w-4" />
              Seed Test Data (47 users)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test Results */}
      {testResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Test Results ({testResults.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {testResults.map((result, idx) => (
                  <Card key={idx} className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="space-y-1">
                        <p className="font-semibold">{result.testUser.name}</p>
                        <p className="text-sm text-muted-foreground">{result.testUser.email}</p>
                        <p className="text-xs text-muted-foreground">{result.testUser.domain}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge variant="default">
                          {result.testType}
                        </Badge>
                        {result.results.score > 0 && (
                          <>
                            <Badge variant="secondary">
                              {result.results.score}/10
                            </Badge>
                            <Badge variant="outline">
                              {result.results.band}
                            </Badge>
                          </>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {result.duration}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-3 space-y-1 bg-muted/30 p-3 rounded-md">
                      {result.log.map((log: string, logIdx: number) => (
                        <p key={logIdx} className="text-xs text-muted-foreground font-mono">
                          {log}
                        </p>
                      ))}
                    </div>
                    
                    <div className="mt-3 flex gap-2">
                      {result.results.quizId && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => window.open(`/admin?tab=quizzes`, '_blank')}
                        >
                          View Quiz
                        </Button>
                      )}
                      {result.results.leadId && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => window.open(`/admin?tab=leads`, '_blank')}
                        >
                          View Lead
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};